<template>
  <Header :show_back="true" />

  <div class="border-b-[2px] border-postRed py-1">
    <LocationChooser/>
  </div>

  <div v-if="form_config_loading" class="flex justify-center items-center min-h-[450px]">
    <img :src="`/images/bars-scale.svg`" class="h-[50px] text-primary stroke-primary">
  </div>
  <div v-else id="configurable-form" class="w-content-standard mx-auto mb-8 pt-10">
    <div v-if="appStore.form_error_message" class="error-message text-red-500 p-4 border border-red-500 rounded mb-4">
      {{ appStore.form_error_message }}
    </div>

    <div v-if="user_name_for_display" class="text-lg mb-6 border-2 border-primary p-2 rounded-[2px] bg-primary bg-opacity-5"><span class="font-bold">Updating User: </span> {{ user_name_for_display }}</div>

    <FormKit v-if="schema" type="form" v-model="appStore.form" @submit="submit_all_fields" :actions="false"   :config="{ disabled: appStore.is_loading }">
      <FormKitSchema :schema="schema" />

      <ButtonComponent type="submit" class="form-link-button" :loading="appStore.is_loading">
        Submit
      </ButtonComponent>
  
    </FormKit>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import axios from "axios";
import Header from "./Header.vue";
import ButtonComponent from '@/components/ButtonComponent.vue'
import LocationChooser from '@/components/LocationChooser.vue'
import { useRoute, useRouter } from 'vue-router'; // Import useRouter
import * as Sentry from "@sentry/vue";
import { with_stable_keys } from '@/lib/form_schema';

export default {
  name: "ConfigurableForm",
  components: { ButtonComponent,LocationChooser, Header },
    props: ["form_id"],
  setup(props) {
    const appStore = useAppStore();
    const authStore = useAuthStore();
    const schema = ref();
    const route = useRoute();
    const router = useRouter();
    const form_config_loading = ref(true)
    const user_name_for_display = ref()

    onMounted(async () => {
      try {
        appStore.form_error_message = null
        authStore.validate_session()
        appStore.form = null

        // Fetch the form schema
        const response = await axios.get(`/api/form/${props.form_id}`);
        schema.value = with_stable_keys(response.data);

        // Loop over route.query and set values in appStore.form
        Object.entries(route.query).forEach(([key, value]) => {
          appStore.form = { ...appStore.form, [key]: value };
        });

        user_name_for_display.value = route.query.user_name

        console.log(route.query)
        console.log(route.query.user_name)

        form_config_loading.value = false
      } catch (error) {
        form_config_loading.value = false
        console.error("Error fetching form data:", error);
      }
    });

    const submit_all_fields = (formData) => {
      appStore.is_loading = true
      appStore.form_error_message = null
      
      // Start timing the form submission
      const submissionStartTime = Date.now();
      const timingData = {
        frontend_processing_time_ms: 0,
        network_request_time_ms: 0,
        total_time_ms: 0
      };
      
      const data = new FormData();

      // Walk the FormKit form model and serialize to FormData. We can't just send
      // formData.foo verbatim because FormKit's file inputs store an array of
      // { name, file: File } wrappers, and the Pro repeater nests those inside
      // row objects. Recurse so plain scalars become "key=value", FormKit file
      // wrappers become "key=<File>" (single) or "key[]=<File>" (multi), and
      // repeaters become "key[idx][child]=...".
      let filesCount = 0;

      // Realm/proxy-proof file detection. We can't rely on `instanceof File`
      // alone: on some browsers (observed on Mobile Safari) the wrapped File
      // failed the check, which dropped it into the generic-object branch below
      // and silently discarded it (a File has no enumerable own props), so the
      // request went out with no file data at all. Blob is File's superclass;
      // the duck-typed fallback covers any case where the prototype chain is lost.
      const isFileLike = (f) =>
        f instanceof Blob ||
        (f && typeof f === 'object' &&
          typeof f.name === 'string' &&
          typeof f.size === 'number' &&
          typeof f.arrayBuffer === 'function');

      const appendField = (key, value) => {
        if (value === null || value === undefined) {
          return;
        }
        // Safety net: a File/Blob must always be appended directly, never
        // recursed into (its enumerable own props are empty -> it would vanish).
        if (isFileLike(value)) {
          data.append(key, value);
          filesCount += 1;
          return;
        }
        if (Array.isArray(value)) {
          // FormKit file-input model: array of { name, file }
          if (value.length > 0 && value.every((item) => item && typeof item === 'object' && isFileLike(item.file))) {
            // Single-file inputs validate as `file` server-side, so a one-item
            // array must be sent as a scalar key (not key[]) or Laravel sees an
            // array and rejects it. Multi-file inputs keep the key[] shape.
            if (value.length === 1) {
              data.append(key, value[0].file);
              filesCount += 1;
            } else {
              for (const item of value) {
                data.append(key + '[]', item.file);
                filesCount += 1;
              }
            }
            return;
          }
          // Generic array (e.g. repeater rows)
          for (let i = 0; i < value.length; i++) {
            appendField(key + '[' + i + ']', value[i]);
          }
          return;
        }
        if (typeof value === 'object') {
          for (const [k, v] of Object.entries(value)) {
            appendField(key + '[' + k + ']', v);
          }
          return;
        }
        data.append(key, value);
      };
      for (const [key, value] of Object.entries(formData)) {
        appendField(key, value);
      }

      //data.append("upload_two", document.querySelector("input[type='file']").files[0]);
      data.append("location", appStore.location)

      // Calculate frontend processing time
      timingData.frontend_processing_time_ms = Date.now() - submissionStartTime;
      const networkStartTime = Date.now();

      axios
        .post("/api/form", data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          // Calculate timing data
          timingData.network_request_time_ms = Date.now() - networkStartTime;
          timingData.total_time_ms = Date.now() - submissionStartTime;
          
          // Check the status in the response
          if (response.data.status === "failure") {
            const errorMessage = response.data.error_message || "An unknown error occurred.";
            appStore.form_error_message = errorMessage; // Save the error message to display

            // Report error to Sentry with comprehensive context
            Sentry.withScope((scope) => {
              scope.setTag('form_id', props.form_id);
              scope.setTag('submission_type', 'form_submission_failure');
              scope.setTag('error_source', 'frontend');
              scope.setContext('form_submission', {
                form_id: props.form_id,
                user_id: authStore.user?.id || 'unknown',
                user_name: authStore.user?.name || 'unknown',
                location: appStore.location,
                timing: timingData,
                error_message: errorMessage,
                response_data: response.data,
                form_data_keys: Object.keys(formData),
                files_count: filesCount
              });
              Sentry.captureException(new Error(`Form submission failed: ${errorMessage}`));
            });

            // Scroll to the top
            window.scrollTo({
                top: 0,
                behavior: "smooth", // Smooth scroll animation
            });
          } else {
            console.log("Form submitted successfully:", response.data);
            appStore.form_error_message = null; // Clear any previous error
            
            // Report successful submission to Sentry with comprehensive timing
            Sentry.withScope((scope) => {
              scope.setTag('form_id', props.form_id);
              scope.setTag('submission_type', 'form_submission_success');
              scope.setTag('error_source', 'frontend');
              scope.setContext('form_submission', {
                form_id: props.form_id,
                user_id: authStore.user?.id || 'unknown',
                user_name: authStore.user?.name || 'unknown',
                location: appStore.location,
                timing: timingData,
                submission_id: response.data.form_submission_id,
                form_data_keys: Object.keys(formData),
                files_count: filesCount,
                response_data: response.data
              });
              Sentry.captureMessage('Form submission completed successfully', 'info');
            });

            router.push({
              name: "Home",
              query: { message: "Form submitted successfully." },
            });
          }
        })
        .catch((error) => {
          // Calculate timing data even for errors
          timingData.network_request_time_ms = Date.now() - networkStartTime;
          timingData.total_time_ms = Date.now() - submissionStartTime;
          
          console.error("Unexpected error submitting form:", error);

          // Report unexpected error to Sentry with comprehensive context
          Sentry.withScope((scope) => {
            scope.setTag('form_id', props.form_id);
            scope.setTag('submission_type', 'form_submission_unexpected_error');
            scope.setTag('error_source', 'frontend');
            scope.setContext('form_submission', {
              form_id: props.form_id,
              user_id: authStore.user?.id || 'unknown',
              user_name: authStore.user?.name || 'unknown',
              location: appStore.location,
              timing: timingData,
              error_message: error.message,
              error_response: error.response?.data,
              error_status: error.response?.status,
              form_data_keys: Object.keys(formData),
              files_count: filesCount
            });
            Sentry.captureException(error);
          });

          // Handle unexpected errors
          const errorMessage = "An unexpected error occurred. Please try again.";
          appStore.form_error_message = errorMessage;
        })
        .finally(() => {
          appStore.is_loading = false;
        });
    };

    return {
      appStore,
      schema,
      submit_all_fields,
      form_config_loading,
      user_name_for_display
    };
  },
};
</script>

<style scoped>
</style>